// Brand keys match getCardIconPath's PAYMENT_BRANDS.
export type CardBrand = 'visa' | 'mastercard' | 'amex' | 'discover' | 'diners' | 'jcb' | ''

const BRAND_PATTERNS: { brand: Exclude<CardBrand, ''>; re: RegExp }[] = [
  { brand: 'visa', re: /^4/ },
  { brand: 'mastercard', re: /^(5[1-5]|2(2[2-9]|[3-6]|7[01]|720))/ },
  { brand: 'amex', re: /^3[47]/ },
  { brand: 'discover', re: /^(6011|65|64[4-9]|622)/ },
  { brand: 'diners', re: /^(36|38|30[0-5])/ },
  { brand: 'jcb', re: /^35(2[89]|[3-8])/ },
]

export function detectCardBrand(cardNumber: string): CardBrand {
  const digits = cardNumber.replace(/\D/g, '')
  if (!digits) {
    return ''
  }
  const match = BRAND_PATTERNS.find(p => p.re.test(digits))
  return match ? match.brand : ''
}

// Amex groups 4-6-5; everyone else groups in 4s.
export function formatCardNumber(value: string, brand: CardBrand = ''): string {
  const digits = value.replace(/\D/g, '').slice(0, brand === 'amex' ? 15 : 16)
  if (brand === 'amex') {
    return digits.replace(/^(\d{0,4})(\d{0,6})(\d{0,5}).*/, (_, a, b, c) => [a, b, c].filter(Boolean).join(' '))
  }
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim()
}

// Amex CVV is 4 digits, everyone else 3.
export function cvvLength(brand: CardBrand): number {
  return brand === 'amex' ? 4 : 3
}

// Luhn check so obviously-mistyped numbers fail before hitting the gateway.
export function isValidCardNumber(cardNumber: string): boolean {
  const digits = cardNumber.replace(/\D/g, '')
  if (digits.length < 13 || digits.length > 19) {
    return false
  }
  let sum = 0
  let double = false
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = parseInt(digits[i], 10)
    if (double) {
      d *= 2
      if (d > 9) {
        d -= 9
      }
    }
    sum += d
    double = !double
  }
  return sum % 10 === 0
}

export function isTestCustomer(customer?: { email?: string } | null): boolean {
  return !!customer?.email && customer.email.toLowerCase().endsWith('@solvpath.com')
}

export function isTestCardNumber(cardNumber: string): boolean {
  const raw = (import.meta.env.VITE_TEST_CARDS as string | undefined) || ''
  const list = raw
    .split(',')
    .map(s => s.replace(/\D/g, ''))
    .filter(Boolean)
  return list.includes(cardNumber.replace(/\D/g, ''))
}

// "MM/YY" formatter for a single expiry input.
export function formatExpiry(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 4)
  if (digits.length < 3) {
    return digits
  }
  return `${digits.slice(0, 2)}/${digits.slice(2)}`
}
